import type { Job } from "./types";

/**
 * Human-readable names for all LocalDocs job types.
 * Used to display user-friendly labels instead of technical identifiers.
 */
export const JOB_TYPE_NAMES: Record<string, string> = {
  content_update: "Content Update",
  quality_check: "Quality Check",
  auto_fix: "Auto-Fix",
  cleanup: "Cleanup",
  export: "Export",
  crawl_documentation: "Documentation Crawl",
  document_process: "Document Processing",
  ai_discovery: "AI Discovery",
};

/**
 * Gets a human-readable display name for a job.
 *
 * Priority:
 * 1. If job_type has a mapping in JOB_TYPE_NAMES, use that
 * 2. Otherwise, format job_name by removing "Scheduled " prefix and converting to Title Case
 *
 * @param job - The job object
 * @returns A human-readable job name
 *
 * @example
 * // Known job type
 * getJobDisplayName({ job_type: 'content_update', job_name: 'Scheduled content_update' })
 * // Returns: "Content Update"
 *
 * @example
 * // Unknown job type - formats job_name
 * getJobDisplayName({ job_type: 'custom', job_name: 'Scheduled my_custom_job' })
 * // Returns: "My Custom Job"
 */
export function getJobDisplayName(job: Job): string {
  // First, try to get a friendly name from the job_type mapping
  if (JOB_TYPE_NAMES[job.job_type]) {
    return JOB_TYPE_NAMES[job.job_type];
  }

  // Fall back to formatting the job_name
  // Strip "Scheduled " prefix (case-insensitive)
  const baseName = job.job_name.replace(/^scheduled\s+/i, "");

  // Convert snake_case to Title Case
  return baseName.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Determines if a job is a scheduled job (created by pg_cron).
 * Scheduled jobs have "Scheduled " prefix in their job_name.
 *
 * @param job - The job object
 * @returns true if the job is a scheduled job, false otherwise
 *
 * @example
 * isScheduledJob({ job_name: 'Scheduled content_update' })
 * // Returns: true
 *
 * @example
 * isScheduledJob({ job_name: 'Manual export' })
 * // Returns: false
 */
export function isScheduledJob(job: Job): boolean {
  return job.job_name.toLowerCase().startsWith("scheduled ");
}
